/**
 * Distribuição de lucros — registro, retenção (Lei 15.270/2025), recibo e
 * contabilização. Os recibos saem em lote: um documento com a competência
 * inteira, pronto para enviar ao cliente.
 */
import { useEffect, useState } from 'react';
import { PageHeader, Loading, Empty } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, competenciaLabel } from '../lib/format';

const API = import.meta.env.VITE_API_URL || '/api';
const API_HOST = import.meta.env.VITE_API_HOST || '';
const auth = () => {
  const t = localStorage.getItem('atrio_jwt');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const req = async (path, opts = {}) => {
  const r = await fetch(API + path, {
    ...opts, headers: { 'Content-Type': 'application/json', ...auth(), ...(opts.headers || {}) },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.message || d.error || 'Falha na requisição');
  return d;
};

const VAZIO = {
  socio_nome: '', socio_cpf: '', socio_participacao: '',
  data_pagamento: new Date().toISOString().slice(0, 10),
  competencia_lucro: '', valor_bruto: '', forma_pagamento: '', observacao: '',
};

export default function DistribuicaoLucros({ clienteId }) {
  const [comp, setComp] = useState(mesAtual());
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(VAZIO);
  const [aberto, setAberto] = useState(false);
  const [sim, setSim] = useState(null);
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [obrig, setObrig] = useState(null);
  // socios vem do cadastro (GESTHUB); nada de nome digitado solto
  const [socios, setSocios] = useState([]);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    req(`/distribuicao/${clienteId}?competencia=${comp}`)
      .then(d => { setDados(d); setSel(new Set()); })
      .catch(e => setErro(e.message)).finally(() => setLoading(false));
    req(`/distribuicao/${clienteId}/obrigacoes`).then(setObrig).catch(() => setObrig(null));
    req(`/distribuicao/${clienteId}/socios`).then(d => setSocios(d.items || [])).catch(() => setSocios([]));
  };
  useEffect(carregar, [clienteId, comp]);

  // Registra (ou desfaz) o pagamento da DARF 1841 desta retencao.
  const marcarDarfPago = async (o) => {
    const data = window.prompt('Data do pagamento da DARF (AAAA-MM-DD). Deixe em branco para desfazer.',
      o.darf_pago_em || String(o.darf_vencimento).slice(0, 10));
    if (data === null) return;
    const observacao = data ? (window.prompt('De onde vem a informação? (comprovante, extrato, aviso do cliente)', '') || '') : '';
    try {
      await req(`/distribuicao/${clienteId}/${o.id}/darf-pago`, {
        method: 'POST', body: JSON.stringify({ data: data.trim(), observacao }) });
      carregar();
    } catch (e) { setErro(e.message); }
  };

  const escolherSocio = (nome) => {
    const s = socios.find(x => x.nome === nome);
    setForm(f => ({ ...f, socio_nome: s?.nome || '', socio_cpf: s?.cpf || '',
      socio_participacao: s?.participacao != null ? String(s.participacao).replace('.', ',') : '' }));
  };
  // Socio unico: o formulario ja abre com ele.
  useEffect(() => {
    if (aberto && socios.length === 1 && !form.socio_nome) escolherSocio(socios[0].nome);
  }, [aberto, socios]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const num = (v) => Number(String(v).replace(/\./g, '').replace(',', '.')) || 0;

  // Simula a retenção enquanto digita valor/data (mostra o líquido antes de gravar)
  useEffect(() => {
    const bruto = num(form.valor_bruto);
    if (!clienteId || !bruto || !form.data_pagamento) { setSim(null); return; }
    const t = setTimeout(() => {
      req(`/distribuicao/${clienteId}/simular`, {
        method: 'POST',
        body: JSON.stringify({ socio_nome: form.socio_nome, socio_cpf: form.socio_cpf,
                               data_pagamento: form.data_pagamento, valor_bruto: bruto }),
      }).then(setSim).catch(() => setSim(null));
    }, 400);
    return () => clearTimeout(t);
  }, [clienteId, form.valor_bruto, form.data_pagamento, form.socio_cpf, form.socio_nome]);

  const salvar = async () => {
    setErro(null); setMsg(null);
    try {
      await req(`/distribuicao/${clienteId}`, {
        method: 'POST',
        body: JSON.stringify({
          socio_nome: form.socio_nome, socio_cpf: form.socio_cpf || undefined,
          socio_participacao: form.socio_participacao ? num(form.socio_participacao) : undefined,
          data_pagamento: form.data_pagamento,
          competencia_lucro: form.competencia_lucro || undefined,
          valor_bruto: num(form.valor_bruto),
          forma_pagamento: form.forma_pagamento || undefined,
          observacao: form.observacao || undefined,
        }),
      });
      setForm(VAZIO); setSim(null); setAberto(false);
      setMsg('Distribuição registrada.'); carregar();
    } catch (e) { setErro(e.message); }
  };

  const contabilizar = async (id) => {
    setErro(null); setMsg(null);
    try {
      await req(`/distribuicao/${clienteId}/${id}/contabilizar`, { method: 'POST' });
      setMsg('Lançamento contábil gerado a partir da distribuição.'); carregar();
    } catch (e) { setErro(e.message); }
  };

  const gerarReinf = async (id) => {
    setErro(null); setMsg(null);
    try {
      const r = await req(`/distribuicao/${clienteId}/${id}/reinf`, { method: 'POST' });
      setMsg(`R-4010 gerado (competência ${r.per_apur}, natureza ${r.nat_rend}). `
           + `DARF ${r.darf} de ${fmtMoeda(r.valor)} vence em ${fmtData(r.vencimento)}.`);
      carregar();
    } catch (e) { setErro(e.message); }
  };

  const abrirRecibos = (ids) => {
    const qs = ids && ids.length ? `ids=${ids.join(',')}` : `competencia=${comp}`;
    const url = `${API_HOST}${API}/distribuicao/${clienteId}/recibos/html?${qs}`;
    // rota autenticada: busca com token e abre em aba nova
    fetch(url, { headers: auth() })
      .then(r => r.text())
      .then(html => {
        const w = window.open('', '_blank');
        w.document.write(html); w.document.close();
      })
      .catch(e => setErro('Falha ao gerar recibos: ' + e.message));
  };

  if (!clienteId) return <Empty title="Selecione um cliente" sub="Use o seletor no topo." />;

  const itens = dados?.items || [];
  const r = dados?.resumo || {};
  const toggle = (id) => setSel(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <>
      <PageHeader
        title="Distribuição de lucros"
        sub="Registro, retenção da Lei 15.270/2025, recibo e contabilização"
        actions={
          <>
            <button onClick={() => abrirRecibos([...sel])} disabled={itens.length === 0}
              title={itens.length === 0 ? `Nenhuma distribuição em ${competenciaLabel(comp)}. Troque o mês, ou use o recibo da linha em Obrigações.` : undefined}
              title={sel.size ? `Gerar ${sel.size} recibo(s) selecionado(s)` : 'Gerar os recibos da competência num único documento'}>
              🧾 {sel.size ? `Recibos (${sel.size})` : 'Recibos da competência'}
            </button>
            <button className="primary" onClick={() => setAberto(v => !v)}>
              {aberto ? '▴ Fechar' : '＋ Nova distribuição'}
            </button>
          </>
        }
      />

      <div style={{ marginBottom: 14 }}>
        <input type="month" value={comp} onChange={e => e.target.value && setComp(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 9, border: '1px solid var(--border-strong)',
            background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'inherit' }} />
      </div>

      {msg && <div className="card mb-2" style={{ borderLeft: '3px solid #22C55E' }}>{msg}</div>}
      {erro && <div className="card mb-2" style={{ borderLeft: '3px solid #EF4444' }}>{erro}</div>}

      {aberto && (
        <div className="card mb-2">
          <div className="card__title">Nova distribuição</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10, marginTop: 10 }}>
            <label style={{ gridColumn: 'span 2' }}>Sócio (do cadastro)
              <select value={form.socio_nome} onChange={e => escolherSocio(e.target.value)}>
                <option value="">{socios.length ? '— escolher —' : 'sem sócio no cadastro do GESTHUB'}</option>
                {socios.map(s => <option key={s.nome} value={s.nome}>{s.nome}{s.qualificacao ? ` · ${s.qualificacao}` : ''}</option>)}
              </select></label>
            <label>CPF{(() => {
              const s = socios.find(x => x.nome === form.socio_nome);
              // CPF so se digita quando o cadastro traz mascarado e nao ha historico
              return s && !s.cpf
                ? <input value={form.socio_cpf} onChange={e => set('socio_cpf', e.target.value)}
                    placeholder={s.cpf_mascarado ? `cadastro: ${s.cpf_mascarado}` : 'CPF'} />
                : <input value={form.socio_cpf} readOnly title="Do cadastro" />;
            })()}</label>
            <label>Participação (%)<input value={form.socio_participacao} readOnly title="Do cadastro" /></label>
            <label>Data do pagamento<input type="date" value={form.data_pagamento} onChange={e => set('data_pagamento', e.target.value)} /></label>
            <label>Lucro apurado em<input value={form.competencia_lucro} onChange={e => set('competencia_lucro', e.target.value)} placeholder="26/05 a 31/07/2026" /></label>
            <label>Valor bruto<input value={form.valor_bruto} onChange={e => set('valor_bruto', e.target.value)} placeholder="0,00" /></label>
            <label>Forma de pagamento<input value={form.forma_pagamento} onChange={e => set('forma_pagamento', e.target.value)} /></label>
            <label style={{ gridColumn: 'span 2' }}>Observação interna (não sai no recibo)
              <input value={form.observacao} onChange={e => set('observacao', e.target.value)} /></label>
          </div>

          {sim && (
            <div className="card" style={{ marginTop: 12, borderLeft: '3px solid var(--gold)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
                <div><div className="small muted">Bruto</div><b>{fmtMoeda(num(form.valor_bruto))}</b></div>
                <div><div className="small muted">IRRF ({sim.irrf_aliquota}%)</div>
                  <b style={{ color: sim.irrf_valor > 0 ? '#B45309' : undefined }}>{fmtMoeda(sim.irrf_valor)}</b></div>
                <div><div className="small muted">Líquido a pagar</div>
                  <b style={{ color: '#16A34A' }}>{fmtMoeda(sim.valor_liquido)}</b></div>
                {sim.ja_recebido_no_mes > 0 && (
                  <div><div className="small muted">Já recebido no mês</div><b>{fmtMoeda(sim.ja_recebido_no_mes)}</b></div>
                )}
              </div>
              <p className="small muted" style={{ marginTop: 8 }}>{sim.regra}</p>
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setForm(VAZIO); setSim(null); setAberto(false); }}>Cancelar</button>
            <button className="primary" onClick={salvar}
              disabled={!form.socio_nome || !num(form.valor_bruto)}>Registrar</button>
          </div>
        </div>
      )}

      {obrig && obrig.items?.length > 0 && (
        <div className="card mb-2" style={{ borderLeft: '3px solid #B45309' }}>
          <div className="card__title">Obrigações geradas pela retenção</div>
          <p className="small muted" style={{ margin: '4px 0 10px' }}>
            Distribuição com IRRF retido gera EFD-Reinf R-4010 (natureza 12001, Lucro e Dividendo)
            e DARF 1841-01. O imposto é recolhido pela empresa, não pelo sócio.
          </p>
          <table className="data">
            <thead><tr>
              <th>Recibo</th><th>Sócio</th><th className="num">IRRF</th>
              <th>DARF</th><th>Vencimento</th><th>EFD-Reinf</th><th>Contábil</th><th></th>
            </tr></thead>
            <tbody>
              {obrig.items.map(o => (
                <tr key={o.id}>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{o.recibo_numero}</td>
                  <td>{o.socio_nome}</td>
                  <td className="num bold" style={{ color: '#B45309' }}>{fmtMoeda(o.irrf_valor)}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{o.darf_codigo}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {fmtData(o.darf_vencimento)}{' '}
                    {o.darf_pago_em ? (
                      <span className="badge badge--success" style={{ fontSize: 10, cursor: 'pointer' }}
                        title={(o.darf_pago_obs || 'Pagamento registrado') + ' · clique para alterar'}
                        onClick={() => marcarDarfPago(o)}>
                        pago em {fmtData(o.darf_pago_em)}
                      </span>
                    ) : (
                      <>
                        <span className={`badge ${o.dias_para_vencer < 0 ? 'badge--danger'
                          : o.dias_para_vencer <= 10 ? 'badge--warning' : 'badge--neutral'}`}
                          style={{ fontSize: 10 }}>
                          {o.dias_para_vencer < 0 ? `vencido há ${-o.dias_para_vencer}d` : `em ${o.dias_para_vencer}d`}
                        </span>{' '}
                        <button className="ghost" style={{ fontSize: 11 }} onClick={() => marcarDarfPago(o)}
                          title="Registrar que a DARF foi paga">marcar pago</button>
                      </>
                    )}
                  </td>
                  <td>
                    {o.reinf_pendente
                      ? <span className="badge badge--warning">pendente</span>
                      : <span className="badge badge--success">R-4010 {o.r4010_status}</span>}
                  </td>
                  <td>
                    {o.contabil_pendente
                      ? <span className="badge badge--warning">não contabilizada</span>
                      : <span className="badge badge--success">lançada</span>}
                  </td>
                  <td>
                    <button className="ghost" onClick={() => abrirRecibos([o.id])}
                      title={`Recibo ${o.recibo_numero}`}>🧾 Recibo</button>
                    {o.reinf_pendente && (
                      <button className="ghost" onClick={() => gerarReinf(o.id)}
                        title="Gera o evento R-4010 desta retenção">Gerar R-4010</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="kpi-grid kpi-grid--compact" style={{ marginBottom: 14 }}>
        <div className="kpi-card"><p className="kpi-card__label">Distribuições em {competenciaLabel(comp)}</p>
          <p className="kpi-card__value">{r.qtd || 0}</p></div>
        <div className="kpi-card"><p className="kpi-card__label">Bruto</p>
          <p className="kpi-card__value">{fmtMoeda(r.bruto)}</p></div>
        <div className="kpi-card"><p className="kpi-card__label">IRRF retido</p>
          <p className="kpi-card__value" style={{ color: '#B45309' }}>{fmtMoeda(r.irrf)}</p></div>
        <div className="kpi-card"><p className="kpi-card__label">Líquido pago</p>
          <p className="kpi-card__value" style={{ color: '#16A34A' }}>{fmtMoeda(r.liquido)}</p></div>
        {obrig?.total_irrf > 0 && (
          <div className="kpi-card"><p className="kpi-card__label">IRRF em aberto (DARF 1841-01)</p>
            <p className="kpi-card__value" style={{ color: '#B45309' }}>{fmtMoeda(obrig.total_irrf)}</p>
            {obrig.total_irrf_pago > 0 && <p className="small muted" style={{ margin: 0 }}>já recolhido: {fmtMoeda(obrig.total_irrf_pago)}</p>}</div>
        )}
      </div>

      {loading ? <Loading /> : itens.length === 0 ? (
        <Empty title={`Nenhuma distribuição em ${competenciaLabel(comp)}`}
               sub="Use + Nova distribuição, ou troque a competência." />
      ) : (
        <div className="card">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Pagamento</th><th>Recibo</th><th>Sócio</th>
                <th className="num">Bruto</th><th className="num">IRRF</th><th className="num">Líquido</th>
                <th>Status</th><th>Contábil</th><th style={{ width: 130 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {itens.map(d => (
                <tr key={d.id}>
                  <td><input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtData(d.data_pagamento)}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{d.recibo_numero}</td>
                  <td>{d.socio_nome}</td>
                  <td className="num bold">{fmtMoeda(d.valor_bruto)}</td>
                  <td className="num" style={{ color: Number(d.irrf_valor) > 0 ? '#B45309' : undefined }}>
                    {Number(d.irrf_valor) > 0 ? fmtMoeda(d.irrf_valor) : '0,00'}</td>
                  <td className="num bold" style={{ color: '#16A34A' }}>{fmtMoeda(d.valor_liquido)}</td>
                  <td><span className={`badge ${d.status === 'pago' ? 'badge--success' : 'badge--warning'}`}>{d.status}</span></td>
                  <td className="small muted">{d.lancamento_id ? `lcto #${d.lcto_numero ?? d.lancamento_id}` : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="ghost" onClick={() => abrirRecibos([d.id])} title="Recibo desta distribuição">🧾</button>
                    {!d.lancamento_id && (
                      <button className="ghost" onClick={() => contabilizar(d.id)}
                        title="Gera o lançamento contábil (lucros / sócio / IRRF a recolher)">⇄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
